/*
 * PD Image Gallery — fullscreen viewer (GRG-29 #232).
 *
 * The gallery section (sections/pd-image-gallery.liquid, GRG-57) rendered its
 * Cloudinary images as plain <img>s inside swiper slides, so clicking one did
 * nothing — GRG-28's AC asks for a fullscreen view. The theme had no lightbox
 * to reuse, so this is it.
 *
 * Layout follows Figma 4818-23580 (both breakpoints): the photo sits directly on
 * a blurred rgba(0,0,0,.75) wash with its capture details centred underneath.
 * That frame carries no on-screen arrows or counter, so paging is keyboard
 * (left/right) and touch swipe only.
 *
 * Markup contract (all inside <pd-image-gallery>):
 *   [data-pd-gallery-open]     one button per slide. Carries the full-size
 *                              src/srcset/alt plus the caption fields, so
 *                              nothing large is downloaded until the viewer
 *                              actually opens.
 *   [data-pd-gallery-dialog]   native <dialog>. showModal() gives us the focus
 *                              trap, Esc-to-close and inert background for
 *                              free — no hand-rolled focus management.
 *   [data-pd-gallery-image]    the <img> the viewer paints into.
 *   [data-pd-gallery-caption]  wrapper, plus -camera-line / -lens-line and
 *                              their -value spans.
 *   [data-pd-gallery-close]    close control.
 *
 * Visibility is toggled with the `hidden` CLASS: Dawn ships
 * .hidden{display:none!important} in the `dawn` layer, and layered !important
 * resolves earliest-layer-wins, so it beats the element's own flex/inline
 * utility. The `hidden` attribute would not.
 *
 * Navigation wraps around, matching the carousel behind it.
 */
if (!customElements.get('pd-image-gallery')) {
  customElements.define(
    'pd-image-gallery',
    class PdImageGallery extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-pd-gallery-dialog]');
        this.image = this.querySelector('[data-pd-gallery-image]');
        this.caption = this.querySelector('[data-pd-gallery-caption]');
        this.cameraLine = this.querySelector('[data-pd-gallery-camera-line]');
        this.cameraValue = this.querySelector('[data-pd-gallery-camera-value]');
        this.lensLine = this.querySelector('[data-pd-gallery-lens-line]');
        this.lensValue = this.querySelector('[data-pd-gallery-lens-value]');
        this.triggers = Array.from(this.querySelectorAll('[data-pd-gallery-open]'));

        // No <dialog> support (or markup missing) — leave the buttons inert
        // rather than half-wiring a viewer that can't open.
        if (!this.dialog || !this.image || typeof this.dialog.showModal !== 'function') return;

        this.index = 0;

        this.triggers.forEach((trigger, i) => {
          trigger.addEventListener('click', () => this.open(i));
        });

        this.querySelectorAll('[data-pd-gallery-close]').forEach((el) => {
          el.addEventListener('click', () => this.dialog.close());
        });

        this.dialog.addEventListener('keydown', (evt) => {
          if (evt.key === 'ArrowLeft') {
            evt.preventDefault();
            this.step(-1);
          } else if (evt.key === 'ArrowRight') {
            evt.preventDefault();
            this.step(1);
          }
        });

        // Click anywhere that is not the photo, the caption or a control
        // closes. Matching on what was NOT hit (rather than on the stage alone)
        // means the empty wash beside a portrait photo closes too.
        this.dialog.addEventListener('click', (evt) => {
          const target = evt.target;
          if (target.closest && target.closest('img, figcaption, button')) return;
          this.dialog.close();
        });

        this.bindSwipe();

        this.dialog.addEventListener('close', () => {
          document.documentElement.style.overflow = this._prevOverflow || '';
          this._prevOverflow = undefined;

          // Native focus restore targets whatever was focused before
          // showModal(), which is <body> when the viewer was opened by a
          // pointer click — a click doesn't reliably focus the button. Put
          // focus back on the image the shopper opened so keyboard users
          // resume where they left off instead of at the top of the page.
          if (this._opener && typeof this._opener.focus === 'function') {
            this._opener.focus();
          }
        });
      }

      open(index) {
        this._opener = this.triggers[index] || null;

        // showModal() makes the background inert but does not stop it
        // scrolling. Guarded so a reopen that races the (asynchronous) close
        // event can't record 'hidden' as the value to restore.
        if (this._prevOverflow === undefined) {
          this._prevOverflow = document.documentElement.style.overflow;
        }
        document.documentElement.style.overflow = 'hidden';

        this.show(index);
        this.dialog.showModal();

        // showModal() autofocuses the first focusable child (the close button),
        // and Chrome counts that as :focus-visible — which painted a ring on the
        // bare X the design calls for. Park focus on the dialog instead: still
        // inside the modal, so Esc and the focus trap behave, and the first Tab
        // reaches the close button with a ring that is genuinely wanted.
        if (typeof this.dialog.focus === 'function') this.dialog.focus();
      }

      step(delta) {
        if (this.triggers.length < 2) return;
        const count = this.triggers.length;
        this.show((this.index + delta + count) % count);
      }

      show(index) {
        const trigger = this.triggers[index];
        if (!trigger) return;
        this.index = index;

        this.image.src = trigger.dataset.pdGallerySrc || '';
        const srcset = trigger.dataset.pdGallerySrcset;
        if (srcset) this.image.srcset = srcset;
        this.image.alt = trigger.dataset.pdGalleryAlt || '';

        this.showCaption(trigger.dataset.pdGalleryCamera, trigger.dataset.pdGalleryLens);
      }

      // Either line can be missing — the whole caption block collapses when
      // both are, so an image with no capture details just shows the photo.
      showCaption(camera, lens) {
        if (!this.caption) return;

        const line = (wrapper, value, target) => {
          if (!wrapper) return false;
          const present = Boolean(value);
          if (present && target) target.textContent = value;
          wrapper.classList.toggle('hidden', !present);
          return present;
        };

        const hasCamera = line(this.cameraLine, camera, this.cameraValue);
        const hasLens = line(this.lensLine, lens, this.lensValue);
        this.caption.classList.toggle('hidden', !hasCamera && !hasLens);
      }

      // Lightweight horizontal swipe. Swiper isn't reused here: the viewer
      // holds one <img> at a time (so the other full-size images are never
      // fetched), which leaves nothing for a carousel to slide.
      bindSwipe() {
        let startX = null;
        let startY = null;

        this.dialog.addEventListener(
          'touchstart',
          (evt) => {
            const touch = evt.changedTouches[0];
            startX = touch.clientX;
            startY = touch.clientY;
          },
          { passive: true }
        );

        this.dialog.addEventListener(
          'touchend',
          (evt) => {
            if (startX === null) return;
            const touch = evt.changedTouches[0];
            const dx = touch.clientX - startX;
            const dy = touch.clientY - startY;
            startX = null;
            startY = null;
            // Horizontal intent only, so a vertical flick doesn't page.
            if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
            this.step(dx < 0 ? 1 : -1);
          },
          { passive: true }
        );
      }
    }
  );
}
